/**
 * Featured Solution tag visibility.
 *
 * Editors attach up to three timed tags to a Featured Solution card through the
 * "Featured Solution Tags" Contentstack custom field. Every stored tag carries an
 * absolute UTC `expiresAt`, so the storefront needs no date or timezone logic:
 * a tag shows while that instant is still in the future.
 *
 * The field can also be written through the Contentstack API without the
 * extension's validation, so everything here is defensive about the stored shape.
 */

/** UID of the custom field on each card object in the `feature_solutions` group (confirmed from live CMS data). */
export const SOLUTION_TAGS_FIELD = 'solution_tags';

/** Most tags a card renders, counted after expired and unknown tags are dropped. */
export const MAX_FEATURED_SOLUTION_TAGS = 3;

export type TagType = 'pre_order' | 'low_lead_time' | 'limited_availability' | 'new' | 'most_popular' | 'best_overall';

/**
 * Color family, mapped to the `$exx-color-status-*` tokens in `_v-colors.scss`.
 *  - scheduled: purple
 *  - active:    blue
 *  - attention: yellow
 *  - success:   green
 *  - inactive:  grey
 */
export type TagTone = 'scheduled' | 'active' | 'attention' | 'success' | 'inactive';

export interface TagTypeDef {
    label: string;
    /** `svgIcon` name registered in `app-icons.ts`. */
    icon: string;
    tone: TagTone;
}

export const TAG_TYPES: Readonly<Record<TagType, TagTypeDef>> = {
    pre_order: { label: 'Pre-Order', icon: 'calendar', tone: 'scheduled' },
    low_lead_time: { label: 'Low Lead Time', icon: 'clock', tone: 'active' },
    limited_availability: { label: 'Limited Availability', icon: 'alert-triangle', tone: 'inactive' },
    new: { label: 'New', icon: 'star', tone: 'attention' },
    most_popular: { label: 'Most Popular', icon: 'flame', tone: 'success' },
    best_overall: { label: 'Best Overall', icon: 'award', tone: 'success' },
};

export interface ActiveTag extends TagTypeDef {
    type: TagType;
    expiresAt: string;
}

interface StoredTag {
    type?: unknown;
    expiresAt?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isTagType(value: unknown): value is TagType {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TAG_TYPES, value);
}

/**
 * Mirrors `isTagActive` in the extension: a tag renders while `Date.parse(expiresAt) > now`.
 * Anything unparseable, missing, or of an unknown type counts as expired. The stored
 * `mode` (duration vs custom date) is irrelevant here; `expiresAt` is the single source of truth.
 */
export function isTagActive(tag: unknown, now: number): boolean {
    if (!isRecord(tag)) return false;
    const { type, expiresAt } = tag as StoredTag;
    if (!isTagType(type)) return false;
    if (typeof expiresAt !== 'string') return false;
    const expires = Date.parse(expiresAt);
    return Number.isFinite(expires) && expires > now;
}

/**
 * Turns the raw custom-field value into the tags a card should render right now.
 *
 * Expired and unknown tags are dropped first, then later duplicates of a type already
 * present, then the list is capped. The cap applies after filtering so an expired tag
 * never crowds out a live one.
 */
export function getActiveTags(rawField: unknown, now: number = Date.now(), max: number = MAX_FEATURED_SOLUTION_TAGS): ActiveTag[] {
    if (!isRecord(rawField) || !Array.isArray(rawField.tags)) return [];

    const seen = new Set<TagType>();
    const active: ActiveTag[] = [];
    for (const tag of rawField.tags) {
        if (active.length >= max) break;
        if (!isTagActive(tag, now)) continue;
        const { type, expiresAt } = tag as { type: TagType; expiresAt: string };
        if (seen.has(type)) continue;
        seen.add(type);
        active.push({ type, expiresAt, ...TAG_TYPES[type] });
    }
    return active;
}

/**
 * Whether any card in a row has at least one tag to show. Computed from the filtered
 * lists, not the raw data, so a row whose tags have all expired stops reserving tag space.
 */
export function rowHasActiveTags(solutions: unknown[], now: number = Date.now()): boolean {
    if (!Array.isArray(solutions)) return false;
    return solutions.some((solution) => isRecord(solution) && getActiveTags(solution[SOLUTION_TAGS_FIELD], now).length > 0);
}
